// Everything the result pages derive from a score result: grades, check results, the fix ranking,
// counts, the improvements filter and the CSV. Pure functions, covered by tests/report.test.mjs.

// How many checks the audit runs, rounded down for copy ("50+ checks"). Used where there is no result to count
// from: the home page and the email box. The full report counts the checks in the result itself.
export const AUDIT_CHECK_COUNT = '50+'

// In audit order. `key` is the category name in the result JSON; `label` and `phrase` override the
// sentence-case display name and its in-sentence form where an acronym needs its capitals.
export const CATEGORIES = Object.freeze([
  { key: 'Accessibility', icon: 'person-arms-spread', description: 'Can everyone fill it in? Contrast, label size, spacing, keyboard.' },
  { key: 'Usability', icon: 'hand-tap', description: 'How easy it is to finish on any device: autocomplete, focus, field count.' },
  { key: 'Validation', icon: 'shield-check', description: 'Clear required fields and errors while the context is still fresh.' },
  { key: 'Style', icon: 'layout', description: 'Layout, alignment, labels above fields, consistent fonts.' },
  { key: 'Web Standards', icon: 'code', description: 'Correct field types, labels and form markup.' },
  { key: 'Performance', icon: 'gauge', description: 'How fast the page with your form loads and responds.' },
  { key: 'Privacy', icon: 'shield', description: 'Secure password fields and reassuring microcopy for sensitive data.' },
  { key: 'AI Readiness', label: 'AI readiness', phrase: 'AI readiness', icon: 'robot', description: 'Whether AI assistants can understand your form and fill it in for your prospects.' },
  { key: 'E-Commerce', icon: 'shopping-cart-simple', description: 'Guest checkout and payment options, for checkout forms.' }
])

// Category cards in the full report, as two columns.
export const CATEGORY_COLUMNS = Object.freeze([
  ['Accessibility', 'Validation', 'Style', 'Privacy'],
  ['Usability', 'Web Standards', 'Performance', 'AI Readiness', 'E-Commerce']
])

const categoryIndex = (name) => {
  const index = CATEGORIES.findIndex((category) => category.key === name)
  return index === -1 ? CATEGORIES.length : index
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

// 8 => "eight", for copy like "eight categories"; digits above ten.
export const countWord = (count) => NUMBER_WORDS[count] ?? String(count)

export const getCategoryMeta = (name) => CATEGORIES.find((category) => category.key === name) ?? { key: name, icon: 'list-checks', description: '' }

// "Web Standards" => "Web standards", "E-Commerce" => "E-commerce"
export const toSentenceCase = (text) => text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : ''

// The display name of a category: "Web standards", "AI readiness".
export const getCategoryLabel = (name) => getCategoryMeta(name).label ?? toSentenceCase(name)

// A category name inside a sentence: "web standards", "AI readiness".
export const getCategoryPhrase = (name) => getCategoryMeta(name).phrase ?? name.toLowerCase()

// The category of the AI readiness check, which always gets a place among the top fixes when it isn't passed.
export const AI_READINESS_CATEGORY = 'AI Readiness'

// One decimal at most, without a trailing ".0": 7.14 => "7.1", 8 => "8"
export const formatScore = (score) => (Math.round(Number.parseFloat(score) * 10) / 10).toString()

export const isNumeric = (value) => value !== null && value !== undefined && value !== '' && !Number.isNaN(Number.parseFloat(value))

export const isScoredCategory = (category) => isNumeric(category?.score)

export const hasCheckScore = (check) => !!check.score || (check.score === 0 && check.weight !== null && check.weight >= 0)

// Ring, chips and badges: 8 and above good, 5 to 8 mid, below 5 low.
export const getScoreLevel = (score) => {
  const value = Number.parseFloat(score)
  if (value >= 8) return 'good'
  if (value >= 5) return 'mid'
  return 'low'
}

// Lower bound included. `width` is the share of the 0 to 10 scale, for the scale strip.
export const GRADES = Object.freeze([
  {
    key: 'poor',
    label: 'Poor',
    min: 0,
    width: 5,
    chip: 'poor',
    headline: 'This form is losing people.',
    line: () => ({ before: 'Key best practices are missing. The fixes at the top of the list will make the biggest difference.' })
  },
  {
    key: 'needs-work',
    label: 'Needs work',
    min: 5,
    width: 2,
    chip: 'fair',
    headline: 'Your visitors are working too hard.',
    line: (weak) => ({ before: 'Several best practices are missing. Start with ', weak, after: '.' })
  },
  {
    key: 'fair',
    label: 'Fair',
    min: 7,
    width: 1,
    chip: 'fair',
    headline: 'Good bones. Room to grow.',
    line: (weak) => ({ before: 'Most things work, but ', weak, after: ' are costing you completions.' })
  },
  {
    key: 'good',
    label: 'Good',
    min: 8,
    width: 1,
    chip: 'good',
    headline: 'Strong form. A few quick wins left.',
    line: () => ({ before: 'The basics are right. The checks below are what stands between you and the last completions.' })
  },
  {
    key: 'excellent',
    label: 'Excellent',
    min: 9,
    width: 1,
    chip: 'excellent',
    headline: 'Best in class.',
    line: () => ({ before: 'Your form follows nearly every best practice we check. Keep it that way as your site changes.' })
  }
])

// The scored categories below 7, lowest first (two at most), or the lowest one if none is below 7.
export const getWeakCategories = (details = {}) => {
  const scored = Object.entries(details)
    .filter(([, category]) => isScoredCategory(category))
    .map(([name, category]) => ({ name, score: Number.parseFloat(category.score) }))
    .sort((a, b) => a.score - b.score || categoryIndex(a.name) - categoryIndex(b.name))

  const weak = scored.filter((category) => category.score < 7).slice(0, 2)
  return (weak.length ? weak : scored.slice(0, 1)).map((category) => getCategoryPhrase(category.name)).join(' and ')
}

export const getScoreGrade = (score, details = {}) => {
  const value = Number.parseFloat(score)
  const grade = [...GRADES].reverse().find((band) => value >= band.min) ?? GRADES[0]
  return { ...grade, line: grade.line(getWeakCategories(details)) }
}

export const CHECK_RESULTS = Object.freeze({
  passed: { key: 'passed', label: 'Passed', segments: 4 },
  almost: { key: 'almost', label: 'Almost', segments: 3 },
  'needs-work': { key: 'needs-work', label: 'Needs work', segments: 2 },
  failed: { key: 'failed', label: 'Failed', segments: 1 },
  tip: { key: 'tip', label: 'Tip', segments: 0 }
})

export const getCheckResult = (check) => {
  if (!hasCheckScore(check)) return CHECK_RESULTS.tip
  const score = Number.parseFloat(check.score)
  if (score >= 10) return CHECK_RESULTS.passed
  if (score >= 8) return CHECK_RESULTS.almost
  if (score >= 5) return CHECK_RESULTS['needs-work']
  return CHECK_RESULTS.failed
}

const isToFix = (check) => hasCheckScore(check) && getCheckResult(check).key !== 'passed'

export const getAllChecks = (details = {}) => Object.entries(details)
  .flatMap(([category, group]) => (group.checks ?? []).map((check) => ({ ...check, category })))

export const hasChecks = (details = {}) => getAllChecks(details).length > 0

export const countChecks = (details = {}) => {
  const checks = getAllChecks(details)
  return {
    total: checks.length,
    toFix: checks.filter(isToFix).length,
    passed: checks.filter((check) => getCheckResult(check).key === 'passed').length,
    tips: checks.filter((check) => !hasCheckScore(check)).length
  }
}

// Scored checks that did not pass, by weight × (10 − score); ties by weight, then category order.
// An AI readiness check that did not pass always makes the list: if it doesn't rank in on its own, it takes the last place.
export const rankFixes = (details = {}, limit = 5) => {
  const ranked = getAllChecks(details)
    .filter(isToFix)
    .map((check, order) => ({ ...check, order, impact: (Number(check.weight) || 0) * (10 - Number.parseFloat(check.score)) }))
    .sort((a, b) =>
      b.impact - a.impact ||
      (Number(b.weight) || 0) - (Number(a.weight) || 0) ||
      categoryIndex(a.category) - categoryIndex(b.category) ||
      a.order - b.order
    )
  const top = ranked.slice(0, limit)
  const aiReadiness = ranked.find((check) => check.category === AI_READINESS_CATEGORY)
  if (aiReadiness && !top.includes(aiReadiness)) {
    top[limit - 1] = aiReadiness
  }
  return top.map(({ order, ...check }) => check)
}

// Scored checks first, tips last; otherwise the order of the result.
export const sortChecks = (checks = []) => [...checks].sort((a, b) => Number(!hasCheckScore(a)) - Number(!hasCheckScore(b)))

// Without passed checks and tips; categories left empty are dropped.
export const filterImprovements = (details = {}) => Object.fromEntries(
  Object.entries(details)
    .map(([name, group]) => [name, { ...group, checks: (group.checks ?? []).filter(isToFix) }])
    .filter(([, group]) => group.checks.length)
)

export const IMPROVEMENTS_QUERY = 'improvements'

export const withImprovementsQuery = (href, on) => {
  const url = new URL(href)
  if (on) {
    url.searchParams.set(IMPROVEMENTS_QUERY, '1')
  } else {
    url.searchParams.delete(IMPROVEMENTS_QUERY)
  }
  return url.toString()
}

export const isImprovementsQuery = (query = {}) => query[IMPROVEMENTS_QUERY] === '1'

// The backend stores `ts` as "YYYY-MM-DD HH:MM:SS.mmm" in UTC.
export const parseScoredAt = (ts) => {
  if (!ts) return null
  const date = new Date(ts.replace(' ', 'T') + (/[zZ]|[+-]\d\d:?\d\d$/.test(ts) ? '' : 'Z'))
  return Number.isNaN(date.getTime()) ? null : date
}

export const formatScoredDate = (date) => date
  ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date)
  : ''

const ENTITIES = Object.freeze({ amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' })

export const htmlToText = (html = '') => String(html ?? '')
  .replace(/<br\s*\/?>/gi, ' ')
  .replace(/<\/(p|li|div)>/gi, ' ')
  .replace(/<[^>]*>/g, '')
  .replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (match, entity) => ENTITIES[entity])
  .replace(/\s+/g, ' ')
  .trim()

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`

export const buildCsv = (details = {}) => {
  const rows = [['Category', 'Check', 'Score', 'Result', 'Weight', 'Description']]
  for (const check of getAllChecks(details)) {
    rows.push([
      getCategoryLabel(check.category),
      check.name,
      hasCheckScore(check) ? formatScore(check.score) : '',
      getCheckResult(check).label,
      check.weight ?? '',
      htmlToText(check.description)
    ])
  }
  return rows.map((row) => row.map(csvCell).join(',')).join('\r\n')
}

export const getCsvFileName = (uri, date = new Date()) => {
  let domain = 'form'
  try {
    domain = new URL(/^https?:\/\//i.test(uri) ? uri : `https://${uri}`).hostname.replace(/^www\./i, '')
  } catch (e) {}
  return `exatom-score-${domain}-${date.toISOString().slice(0, 10)}.csv`
}
